"use client"

import * as React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

export interface FilterableSelectOption {
    value: string
    label: string
    /** Additional details shown in parentheses (e.g., VAT code, country) */
    details?: string
    /** @deprecated Use `details` instead */
    description?: string
    /** Additional text to search against (not displayed) */
    searchText?: string
}

interface FilterableSelectProps {
    value?: string
    /** Called when the value changes */
    onValueChange?: (value: string) => void
    /** Alias for onValueChange (for easier migration from PartySelect) */
    onChange?: (value: string) => void
    options: FilterableSelectOption[]
    disabled?: boolean
    loading?: boolean
    placeholder?: string
    searchPlaceholder?: string
    emptyMessage?: string
    className?: string
    triggerClassName?: string
    maxVisible?: number
    /** Show a "None" / clear option at the top */
    showClearOption?: boolean
    /** @deprecated Use showClearOption instead */
    showNoneOption?: boolean
    clearOptionLabel?: string
    /** @deprecated Use clearOptionLabel instead */
    noneLabel?: string
    /** Tooltip text to show on hover of the trigger */
    tooltip?: string
    /** Show "Options" header section above the list */
    showOptionsHeader?: boolean
    /** Controlled open state (optional - uses internal state if not provided) */
    open?: boolean
    /** Called when open state changes (optional - uses internal state if not provided) */
    onOpenChange?: (open: boolean) => void
    renderOption?: (option: FilterableSelectOption) => React.ReactNode
    renderSelectedValue?: (option: FilterableSelectOption) => React.ReactNode
    /** Extra props (e.g. data-* attributes) spread onto the trigger button */
    triggerExtraProps?: Record<string, string>
}

export const FilterableSelect = ({
    value,
    onValueChange,
    onChange,
    options,
    disabled = false,
    loading = false,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    emptyMessage = "No results found",
    className,
    triggerClassName,
    maxVisible = 50,
    showClearOption = false,
    showNoneOption,
    clearOptionLabel = "Clear selection",
    noneLabel,
    tooltip,
    showOptionsHeader = false,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    renderOption,
    renderSelectedValue,
    triggerExtraProps
}: FilterableSelectProps): JSX.Element => {
    // Support both controlled and uncontrolled open state
    const [internalOpen, setInternalOpen] = useState(false)
    const isControlled = controlledOpen !== undefined
    const open = isControlled ? controlledOpen : internalOpen
    const setOpen = useCallback(
        (nextOpen: boolean) => {
            if (isControlled) {
                controlledOnOpenChange?.(nextOpen)
            } else {
                setInternalOpen(nextOpen)
            }
        },
        [isControlled, controlledOnOpenChange]
    )

    // Support deprecated prop names for backwards compatibility
    const effectiveShowClearOption = showClearOption || showNoneOption || false
    const effectiveClearOptionLabel = noneLabel || clearOptionLabel

    // Normalize value: when clear option is available and value is empty/undefined,
    // use "none" to keep Radix Select in controlled mode (prevents stale display on reset)
    const effectiveValue = (!value || value === "") && effectiveShowClearOption ? "none" : value

    // Support both onChange and onValueChange
    const handleValueChange = useCallback(
        (newValue: string) => {
            // Handle clear option
            const resolvedValue = newValue === "__clear" ? "none" : newValue
            onValueChange?.(resolvedValue)
            onChange?.(resolvedValue)
        },
        [onValueChange, onChange]
    )

    const [searchTerm, setSearchTerm] = useState("")
    const searchInputRef = useRef<HTMLInputElement | null>(null)

    // Focus search input when dropdown opens
    useEffect(() => {
        if (open) {
            const frame = window.requestAnimationFrame(() => {
                searchInputRef.current?.focus()
            })
            return () => window.cancelAnimationFrame(frame)
        }
        setSearchTerm("")
    }, [open])

    // Keep focus on search input when typing
    useEffect(() => {
        if (open) {
            searchInputRef.current?.focus()
        }
    }, [open, searchTerm])

    // Prevent Radix typeahead from hijacking typed characters
    const handleTriggerKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (open && event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
                event.preventDefault()
                searchInputRef.current?.focus()
            }
        },
        [open]
    )

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()

        const filtered = term
            ? options.filter((opt) => {
                const labelMatch = opt.label.toLowerCase().includes(term)
                // Support both details and deprecated description
                const detailsMatch = (opt.details ?? opt.description)?.toLowerCase().includes(term) ?? false
                const searchTextMatch = opt.searchText?.toLowerCase().includes(term) ?? false
                return labelMatch || detailsMatch || searchTextMatch
            })
            : options

        const limited = filtered.slice(0, maxVisible)

        // Always include the currently selected option for proper SelectValue rendering
        const selected = options.find((opt) => opt.value === value)
        if (selected && !limited.some((opt) => opt.value === selected.value)) {
            return [selected, ...limited]
        }

        return limited
    }, [options, searchTerm, maxVisible, value])

    // Find currently selected option for rendering
    const selectedOption = useMemo(
        () => options.find((opt) => opt.value === value),
        [options, value]
    )

    const triggerRef = useRef<HTMLButtonElement | null>(null)

    // Default render option that shows details in parentheses (like PartySelect did)
    const defaultRenderOption = useCallback((opt: FilterableSelectOption) => {
        const details = opt.details ?? opt.description
        return (
            <div className="flex flex-row">
                <span>{opt.label}</span>
                {details && <span className="text-muted-foreground ml-2">({details})</span>}
            </div>
        )
    }, [])

    const trigger = (
        <SelectTrigger
            ref={triggerRef}
            className={cn("w-full", triggerClassName)}
            onKeyDown={handleTriggerKeyDown}
            {...triggerExtraProps}
        >
            <SelectValue placeholder={loading ? "Loading..." : placeholder}>
                {selectedOption && renderSelectedValue
                    ? renderSelectedValue(selectedOption)
                    : undefined}
            </SelectValue>
        </SelectTrigger>
    )

    return (
        <Select
            open={open}
            onOpenChange={setOpen}
            value={effectiveValue}
            onValueChange={handleValueChange}
            disabled={disabled || loading}
        >
            {tooltip ? (
                <TooltipProvider delayDuration={150}>
                    <Tooltip>
                        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="max-w-xs text-xs">
                            {tooltip}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                trigger
            )}
            <SelectContent
                className={className}
                onCloseAutoFocus={(event) => {
                    // Return focus to the trigger so Tab continues to the next field
                    event.preventDefault()
                    triggerRef.current?.focus()
                }}
                header={
                    <div className="px-2 pt-2 pb-1">
                        <input
                            ref={searchInputRef}
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder={searchPlaceholder}
                            className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onContextMenu={(event) => {
                                // Allow native browser context menu (right-click paste)
                                event.stopPropagation()
                            }}
                            onKeyDown={(event) => {
                                // Allow navigation keys to pass through to Radix Select
                                const navigationKeys = [
                                    "ArrowDown",
                                    "ArrowUp",
                                    "Enter",
                                    "Escape",
                                    "Tab",
                                    "Home",
                                    "End",
                                    "PageUp",
                                    "PageDown"
                                ]

                                if (navigationKeys.includes(event.key)) {
                                    // Let the event bubble up to Radix Select for navigation
                                    return
                                }

                                // Stop propagation for all other keys (typing) to prevent Radix typeahead
                                event.stopPropagation()
                            }}
                        />
                    </div>
                }
            >
                {/* Clear/None option */}
                {effectiveShowClearOption && (
                    <SelectItem value="none" className="text-sm text-muted-foreground">
                        {effectiveClearOptionLabel}
                    </SelectItem>
                )}

                {/* Options header */}
                {showOptionsHeader && !searchTerm && filteredOptions.length > 0 && (
                    <div className="px-2 py-1 text-xs text-neutral-400 uppercase">
                        Options
                    </div>
                )}

                {/* Options list */}
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-sm py-1.5">
                            {renderOption ? renderOption(opt) : defaultRenderOption(opt)}
                        </SelectItem>
                    ))
                ) : (
                    !loading && (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                            {searchTerm ? emptyMessage : "No options available"}
                        </div>
                    )
                )}

                {/* Show count hint when there are more options */}
                {!searchTerm && options.length > maxVisible && (
                    <div className="px-2 py-1 text-xs text-muted-foreground text-center border-t">
                        Showing first {maxVisible} of {options.length}. Type to search.
                    </div>
                )}
            </SelectContent>
        </Select>
    )
}
